import React, {forwardRef} from 'react'
import DataGrid, {
    Column,
    FilterRow,
    Format,
    Grouping,
    HeaderFilter,
    Pager,
    Paging,
    Scrolling,
    Selection,
    Sorting
} from 'devextreme-react/data-grid'

export const getDebtGridVariant = () => {
    const env = process.env.REACT_APP_NAME_ENV
    if (env === 'tatenergosbyt' || env === 'renergy') return env
    return 'default'
}

/** Порядок колонок по региону (Selection включается отдельно). */
export const DEBT_COLUMN_ORDER = {
    // Казань
    tatenergosbyt: ['kodSf', 'rym', 'name_doc', 'nom_sf', 'dat_sf', 'd_c', 'sum_nach', 'sum_opl', 'dat_zadol', 'sum_dolg'],
    // Рязань
    renergy: ['ym', 'sum_dolg', 'dat_sf', 'sum_nach', 'sum_opl', 'name_doc', 'nom_sf'],
    // Гарант и прочие
    default: ['kodSf', 'ym', 'name_doc', 'nom_sf', 'dat_sf', 'sum_nach', 'sum_opl', 'sum_dolg'],
}

const periodCellRender = (e) => {
    if (e.value == null) return ''
    return e.value.toString().endsWith('.1') ? e.value.toString() + '0' : e.value.toString()
}

const moneyFormat = <Format type="currency" precision={2} currency={'RUB'}/>

/**
 * Описания колонок. Caption/width зависят от варианта грида.
 * Колонка попадает в грид только если есть в DEBT_COLUMN_ORDER[variant].
 */
const buildColumnDefs = (variant, docsCellRender) => ({
    kodSf: {
        dataField: 'kodSf',
        caption: variant === 'tatenergosbyt' ? 'Документы для просмотра и скачивания' : 'Документы',
        width: variant === 'tatenergosbyt' ? '133px' : '180px',
        cellRender: docsCellRender,
    },
    rym: {
        dataField: 'rym',
        caption: 'Период потребления',
        dataType: 'string',
        width: '120px',
        cellRender: periodCellRender,
    },
    ym: {
        dataField: 'ym',
        caption: variant === 'renergy' ? 'Период' : 'Период выставления',
        dataType: 'string',
        width: '120px',
        cellRender: periodCellRender,
    },
    name_doc: {
        dataField: 'name_doc',
        caption: 'Тип документа',
        width: '200px',
        dataType: 'string',
    },
    nom_sf: {
        dataField: 'nom_sf',
        caption: variant === 'tatenergosbyt' ? 'Номер документа' : 'Номер документа начисления',
        dataType: 'string',
    },
    dat_sf: {
        dataField: 'dat_sf',
        caption: variant === 'tatenergosbyt' ? 'Дата документа' : 'Дата документа начисления',
        dataType: 'date',
    },
    d_c: {
        dataField: 'd_c',
        caption: 'Дата размещения документа в ЛК',
        dataType: 'date',
    },
    sum_nach: {
        dataField: 'sum_nach',
        caption: 'Начислено с налогом',
        alignment: 'right',
        dataType: 'numeric',
        formatChild: moneyFormat,
    },
    sum_opl: {
        dataField: 'sum_opl',
        caption: variant === 'tatenergosbyt' ? 'Оплаченная сумма' : 'Сумма оплаты',
        alignment: 'right',
        dataType: 'numeric',
        formatChild: moneyFormat,
    },
    dat_zadol: {
        dataField: 'dat_zadol',
        caption: 'Оплатить до',
        dataType: 'date',
    },
    sum_dolg: {
        dataField: 'sum_dolg',
        caption: variant === 'renergy' ? 'К оплате' : 'Задолженность',
        alignment: 'right',
        dataType: 'numeric',
        formatChild: moneyFormat,
    },
})

const DebtDataGrid = forwardRef(function DebtDataGrid({
    dataSource,
    selectedRowKeys,
    onSelectionChanged,
    onContentReady,
    docsCellRender,
    variant = getDebtGridVariant(),
}, ref) {
    const order = DEBT_COLUMN_ORDER[variant] ?? DEBT_COLUMN_ORDER.default
    const defs = buildColumnDefs(variant, docsCellRender)
    const withSelection = variant === 'tatenergosbyt' || variant === 'renergy'

    return (
        <DataGrid
            className='mt20'
            dataSource={dataSource}
            noDataText="Документов по задолженности не найдено"
            keyExpr="kod_sf"
            columnHidingEnabled={false}
            allowColumnReordering={true}
            allowColumnResizing={true}
            rowAlternationEnabled={true}
            showBorders={true}
            wordWrapEnabled={true}
            columnWidth={'auto'}
            width={'100%'}
            selectedRowKeys={selectedRowKeys}
            onSelectionChanged={onSelectionChanged}
            onContentReady={onContentReady}
            ref={ref}
        >
            <Grouping autoExpandAll={false} />
            {withSelection && (
                <Selection
                    mode="multiple"
                    selectAllMode='allPages'
                    showCheckBoxesMode='always'
                />
            )}
            <Scrolling columnRenderingMode="virtual" />
            <Paging defaultPageSize={12} />
            <Pager
                visible={true}
                allowedPageSizes={[5, 12, 20, 'all']}
                displayMode={'full'}
                showPageSizeSelector={true}
                showInfo={true}
                showNavigationButtons={true} />
            <Sorting mode="multiple" />
            <FilterRow visible={true} />
            <HeaderFilter visible={true} />
            {order.map((key) => {
                const col = defs[key]
                if (!col) return null
                const {formatChild, ...props} = col
                return (
                    <Column key={key} {...props}>
                        {formatChild}
                    </Column>
                )
            })}
        </DataGrid>
    )
})

export default DebtDataGrid
